import { TL } from '@/components/design-system'
import { getWrapperClassName } from '@/components/design-system/utils'
import { useScroll } from '@/components/use-scroll'
import { Route } from '@/route'
import { AnchorLink } from 'gatsby-plugin-anchor-links'
import { Link, useI18next } from 'gatsby-plugin-react-i18next'
import throttle from 'lodash.throttle'
import React, { useEffect, useState } from 'react'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { isBrowser } from 'react-device-detect'

type Props = Readonly<{
  className: string
}>

export const Header = (props: Props) => {
  const { languages, originalPath, t, i18n } = useI18next('common')
  const [url, setUrl] = useState('')
  const [urlHash, setUrlHash] = useState('')
  const [estSur404, setEstSur404] = useState(true)
  const [estSurServices, setEstSurServices] = useState(true)
  const [estSurBlog, setEstSurBlog] = useState(true)
  const [estMenuLangueOuvert, setEstMenuLangueOuvert] = useState(false)
  const [mobileMenuClasse, setMobileMenuClasse] = useState('hidden')
  const ref = useDetectClickOutside({ onTriggered: () => setEstMenuLangueOuvert(false) })
  const { scrollY } = useScroll()

  useEffect(() => {
    setUrl(window.location.pathname)
    setUrlHash(window.location.hash)
  }, [])

  useEffect(() => {
    setEstSur404(window.location.href.includes('404'))
    setEstSurServices(window.location.href.includes('/services'))
    setEstSurBlog(window.location.href.includes('/blog'))
    if (isBrowser && !estSur404 && !estSurBlog) {
      const expertises = estSurServices
          ? document.getElementById(Route.SERVICES.replace('#', ''))?.offsetTop!
          : document.getElementById(Route.EXPERTISES.replace('#', ''))?.offsetTop!
      const aPropos = document.getElementById(Route.A_PROPOS.replace('#', ''))?.offsetTop!
      const contact = document.getElementById(Route.CONTACT.replace('#', ''))?.offsetTop!

      if (scrollY <= expertises - 210) {
        onClickLien(Route.SERVICES)
      }
      if (scrollY > expertises - 210 && scrollY <= aPropos - 210) {
        onClickLien(Route.EXPERTISES)
      }
      if (scrollY > aPropos - 210 && scrollY <= contact - 700) {
        onClickLien(Route.A_PROPOS)
      }
      if (scrollY > contact - 700) {
        onClickLien(Route.CONTACT)
      }
    }
  }, [scrollY, estSur404])

  const onClickLien = (menu: string) => {
    throttle(() => window.history.pushState('', '', `${ menu }`), 30000)
    setUrlHash(menu)
    setMobileMenuClasse('hidden')
  }

  const onToggleMobileMenu = () => {
    setMobileMenuClasse(mobileMenuClasse === 'hidden' ? 'visible' : 'hidden')
  }

  const onToggleLangueMenu = () => {
    setEstMenuLangueOuvert(!estMenuLangueOuvert)
  }

  const menuClass = (menu: string, urlCourante: string) => {
    if (estSur404) {
      return 'hover:text-digitalblue'
    }

    if (estSurBlog) {
      if (urlCourante.includes(menu) && menu !== Route.CONTACT) {
        return 'font-bold before:content-[""] before:absolute before:inset-x-0 before:bottom-0 before:border-b-4 before:border-digitalblue'
      }
      return 'hover:text-digitalblue'
    }

    if (urlCourante.includes(menu) || (menu === Route.SERVICES && !urlCourante.includes('#'))) {
      return 'font-bold before:content-[""] before:absolute before:inset-x-0 before:bottom-0 before:border-b-4 before:border-digitalblue'
    }

    return 'hover:text-digitalblue'
  }

  const menuClassMobile = (menu: string, urlCourante: string) => {
    if (estSurBlog) {
      if (urlCourante.includes(menu) && menu !== Route.CONTACT) {
        return 'text-digitalblue'
      }
      return 'hover:text-digitalblue'
    }

    if (urlCourante.includes(menu) || (menu === Route.SERVICES && !urlCourante.includes('#'))) {
      return 'text-digitalblue'
    }

    return 'hover:text-digitalblue'
  }

  const menuLangueClass = (estOuvert: boolean) => {
    return estOuvert ? '' : 'hidden'
  }

  return (
      <header
          className={ getWrapperClassName(props.className, 'fixed flex justify-center z-50 top-0 w-full p-7 max-[1337px]:px-1 max-lg:px-7 max-md:absolute') }>
        <nav
            className="px-11 rounded-3xl w-11/12 max-w-screen-2xl bg-white/90 backdrop-blur shadow-[0_0_57px_rgba(0,0,0,0.1)] max-[1452px]:w-full">
          <div className="flex justify-between w-full">
            <div className="my-5">
              <Link to={ Route.ORIGINE }>
                <TL.Logo width={ 142 } height={ 60 } tooltip={ t('marque')! }/>
              </Link>
            </div>

            <div className="flex items-stretch gap-10 max-md:gap-2">
              { /* Menu desktop */ }
              <ul className="flex items-center gap-1 max-md:hidden">
                <li className="relative h-full">
                  <div className={ `${ menuClass(Route.SERVICES, urlHash) } flex h-full items-center` }>
                    <AnchorLink to={ `/${ i18n.resolvedLanguage }/${ Route.SERVICES }` }
                                className="flex items-center h-full px-2.5"
                                onAnchorLinkClick={ () => onClickLien(Route.SERVICES) }>
                      { t('navigation.services') }
                    </AnchorLink>
                  </div>
                </li>

                <li className="relative h-full">
                  <div className={ `${ menuClass(Route.EXPERTISES, urlHash) } flex h-full items-center` }>
                    <AnchorLink to={ `/${ i18n.resolvedLanguage }/${ Route.EXPERTISES }` }
                                className="flex items-center h-full px-2.5"
                                onAnchorLinkClick={ () => onClickLien(Route.EXPERTISES) }>
                      { t('navigation.expertises') }
                    </AnchorLink>
                  </div>
                </li>

                <li className="relative h-full">
                  <div className={ `${ menuClass(Route.A_PROPOS, urlHash) } flex h-full items-center` }>
                    <AnchorLink to={ `/${ i18n.resolvedLanguage }/${ Route.A_PROPOS }` }
                                className="flex items-center h-full px-2.5"
                                onAnchorLinkClick={ () => onClickLien(Route.A_PROPOS) }>
                      { t('navigation.a-propos') }
                    </AnchorLink>
                  </div>
                </li>

                <li className="relative h-full">
                  <div className={ `${ menuClass(Route.CONTACT, urlHash) } flex h-full items-center` }>
                    <AnchorLink to={ `${ url }${ Route.CONTACT }` }
                                className="flex items-center h-full px-2.5"
                                onAnchorLinkClick={ () => onClickLien(Route.CONTACT) }>
                      { t('navigation.contact') }
                    </AnchorLink>
                  </div>
                </li>

                <li className="relative h-full">
                  <div className={ `${ menuClass(Route.BLOG, url) } flex h-full items-center` }>
                    <Link to={ `/${ Route.BLOG }` }
                          className="flex items-center h-full px-2.5"
                          onClick={ () => onClickLien(Route.BLOG) }>
                      { t('navigation.blog') }
                    </Link>
                  </div>
                </li>

                <li className="relative h-full">
                  <div className="flex h-full items-center">
                    <a className="flex items-center h-full px-2.5"
                       href={ Route.ACADEMY }
                       target="_blank">
                      { t('navigation.academy') }
                    </a>
                  </div>
                </li>
              </ul>

              { /* Menu mobile */ }
              <div className="flex items-center md:hidden">
                <button
                    className="inline-flex items-center -mt-1 text-sm text-gray-500 rounded-lg focus:outline-none"
                    onClick={ onToggleMobileMenu }>
                  <svg className="w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20"
                       xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd"
                          d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                          clipRule="evenodd"></path>
                  </svg>
                </button>
              </div>

              <div ref={ ref } className="relative flex items-center">
                <span className="w-fit font-bold uppercase cursor-pointer" onClick={ onToggleLangueMenu }>
                { estMenuLangueOuvert && <span className="mr-1">↑</span> }
                { !estMenuLangueOuvert && <span className="mr-1">↓</span> }
                <span>{ i18n.resolvedLanguage }</span>
              </span>

                <ul className={ `${ menuLangueClass(estMenuLangueOuvert) } absolute top-16 -right-5 flex flex-col gap-2 rounded-2xl p-6 bg-white shadow-[0_0_10px_rgba(0,0,0,0.25)]` }>
                  { languages.map((lng) => (
                      <li key={ lng }>
                        <Link to={ originalPath }
                              language={ lng }
                              className="uppercase"
                              style={ { color: i18n.resolvedLanguage === lng ? '#2958FE' : 'black' } }>
                          { lng }
                        </Link>
                      </li>
                  )) }
                </ul>
              </div>
            </div>
          </div>

          <ul className={ `flex flex-col w-full pb-4 ${ mobileMenuClasse }` }>
            <li className="py-2">
              <div className={ `${ menuClassMobile(Route.SERVICES, urlHash) } flex w-full` }>
                <AnchorLink to={ `/${ i18n.resolvedLanguage }/${ Route.SERVICES }` }
                            className="w-full"
                            onAnchorLinkClick={ () => onClickLien(Route.SERVICES) }>
                  { t('navigation.services') }
                </AnchorLink>
              </div>
            </li>

            <li className="py-2">
              <div className={ `${ menuClassMobile(Route.EXPERTISES, urlHash) } flex w-full` }>
                <AnchorLink to={ `/${ i18n.resolvedLanguage }/${ Route.EXPERTISES }` }
                            className="w-full"
                            onAnchorLinkClick={ () => onClickLien(Route.EXPERTISES) }>
                  { t('navigation.expertises') }
                </AnchorLink>
              </div>
            </li>

            <li className="py-2">
              <div className={ `${ menuClassMobile(Route.A_PROPOS, urlHash) } flex w-full` }>
                <AnchorLink to={ `/${ i18n.resolvedLanguage }/${ Route.A_PROPOS }` }
                            className="w-full"
                            onAnchorLinkClick={ () => onClickLien(Route.A_PROPOS) }>
                  { t('navigation.a-propos') }
                </AnchorLink>
              </div>
            </li>

            <li className="py-2">
              <div className={ `${ menuClassMobile(Route.CONTACT, urlHash) } flex w-full` }>
                <AnchorLink to={ `${ url }${ Route.CONTACT }` }
                            className="w-full"
                            onAnchorLinkClick={ () => onClickLien(Route.CONTACT) }>
                  { t('navigation.contact') }
                </AnchorLink>
              </div>
            </li>

            <li className="py-2">
              <div className={ `${ menuClassMobile(Route.BLOG, url) } flex w-full` }>
                <Link to={ `/${ Route.BLOG }` }
                      className="w-full"
                      onClick={ () => onClickLien(Route.BLOG) }>
                  { t('navigation.blog') }
                </Link>
              </div>
            </li>

            <li className="py-2">
              <div className="flex w-full">
                <a className="w-full"
                   href={ Route.ACADEMY }
                   target="_blank">
                  { t('navigation.academy') }
                </a>
              </div>
            </li>
          </ul>
        </nav>
      </header>
  )
}

Header.defaultProps = {
  className: ''
}
